// Drawing: level, parts, effects, main loop and small level thumbnails
import Matter from 'matter-js';
import { Registry, CORE } from '../core/registry';
import type { BodyArt, CorePartType, Ctx2D, Layer, ObjState, PartDef, Placement, SimState, WorldDef } from '../core/types';
import { Physics } from '../core/physics';
import { GFX } from '../core/graphics';
import { ctx, $ } from './dom';
import { G, V, DPR, W, H, LEVELS } from './state';
import { drawHint, animHand } from './hud';
import { burst, handleEvents } from './play';
import { drawIntro } from './intro';
import { drawTitle } from './title';
import { openMap } from './map';

function drawFx() {
  for (const f of G.fx) {
    f.x += f.vx; f.y += f.vy; f.vy += f.kind === 'conf' ? 0.03 : 0.18; f.life--; f.rot += 0.1;
    ctx.save(); ctx.globalAlpha = Math.max(0, Math.min(1, f.life / 20));
    ctx.fillStyle = f.col;
    if (f.kind === 'conf') { ctx.translate(f.x, f.y); ctx.rotate(f.rot); ctx.fillRect(-f.r / 2, -f.r / 4, f.r, f.r / 2); }
    else if (f.kind === 'note') { ctx.font = `800 ${f.r * 5}px ${getComputedStyle(document.body).fontFamily}`; ctx.fillText('♪', f.x, f.y); }
    else if (f.kind === 'heart') { ctx.translate(f.x, f.y); ctx.scale(f.r / 6, f.r / 6); ctx.beginPath(); ctx.moveTo(0, 3); ctx.bezierCurveTo(-6, -2, -3, -7, 0, -3); ctx.bezierCurveTo(3, -7, 6, -2, 0, 3); ctx.fill(); }
    else { ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, 7); ctx.fill(); }
    ctx.restore();
  }
  G.fx = G.fx.filter(f => f.life > 0);
}

// ---------- Objects and things (kit and worlds) ----------
// Within a layer, objects are drawn by def.z (default 0); the worlds' things (Thing) after z 0 and before z > 0.
// On level thumbnails, objects with def.thumb (a number) are drawn in the kit's order instead (thumbPass); false = not at all.
function drawCustom(layer: Layer, xIn?: Ctx2D, SIn?: SimState, tIn?: number, thumb?: boolean) {
  const x = xIn || ctx, S = SIn || G.S, t = tIn == null ? G.t : tIn;
  const list = (S.custom || []).filter(st => st.def.draw && (st.def.layer || 'back') === layer && !(thumb && st.def.thumb != null));
  list.sort((a, b) => (a.def.z || 0) - (b.def.z || 0));
  const one = (st: ObjState) => { x.save(); st.def.draw!(x, st, t, GFX, S); x.restore(); };
  for (const st of list) if ((st.def.z || 0) <= 0) one(st);
  for (const th of S.things || []) {
    if ((th.layer || 'mid') !== layer || !th.draw) continue;
    if (th.part && (thumb || G.state === 'build')) continue;
    x.save(); th.draw(x, t, GFX, S); x.restore();
  }
  for (const st of list) if ((st.def.z || 0) > 0) one(st);
}

// Drawing of bodies per kind (def.art), collected from the Registry once
type ArtEntry = { t: string; kind: string; art: BodyArt };
let ARTS: ArtEntry[] | null = null;
function arts(layer: BodyArt['layer']) {
  if (!ARTS) { ARTS = []; for (const d of [Registry.parts, Registry.objs, Registry.goals]) for (const [t, def] of Object.entries<{ art?: Record<string, BodyArt> }>(d)) for (const [kind, art] of Object.entries(def.art || {})) ARTS.push({ t, kind, art }); }
  return ARTS.filter(a => a.art.layer === layer);
}
function drawArt(layer: BodyArt['layer'], x: Ctx2D, S: SimState, build: boolean) {
  const RS: NonNullable<WorldDef['reskin']> = (G.level && Registry.worlds[G.level.wk] || {}).reskin || {};
  for (const { t, kind, art } of arts(layer)) for (const b of S.bodies) {
    if (b.kind !== kind || (build && art.inBuild && !art.inBuild(b))) continue;
    const own = art.reskin && RS[t as CorePartType];
    if (own) own(x, art.reskin!(b), false, GFX, []); else art.draw(x, b, GFX);
  }
}

// Is the placement drawn (or the body instead)?
const showPlacement = (pd: PartDef, build: boolean) => pd.placement === 'never' ? false : pd.placement === 'build' || pd.create ? build : true;

// ---------- Draw a level ----------
// A world can give the kit's parts a new look (world.reskin[type]); the worlds' own parts draw themselves.
export function drawPart(pl: Placement, sel: boolean, ghost?: boolean) {
  const pd = Registry.parts[pl.t]; if (!pd) return;
  const wd = Registry.worlds[G.level && G.level.wk];
  const own = pd.world === CORE && wd && wd.reskin && wd.reskin[pl.t as CorePartType];
  if (own) { own(ctx, pl, sel, GFX, Physics.worldRects(pl)); return; }
  if (pd.draw) pd.draw(ctx, pl, sel, GFX);
}

function drawWorld() {
  const S = G.S, L = G.level, build = G.state === 'build';
  // background + static blocks are cached at screen resolution (shadows are expensive on iPad)
  const k = V.s * DPR;
  if (!G.cache || G.cache.L !== L || G.cache.k !== k || G.cache.v !== GFX.bgVer) {
    const c = document.createElement('canvas'); c.width = Math.ceil(W * k); c.height = Math.ceil(H * k);
    const x = c.getContext('2d')!; x.scale(k, k);
    x.drawImage(GFX.background(L, W, H), 0, 0);
    for (const b of S.bodies) if (b.kind === 'static') GFX.staticBody(x, b);
    drawArt('cache', x, S, false);
    G.cache = { L, k, c, v: GFX.bgVer };
  }
  ctx.drawImage(G.cache.c, 0, 0, W, H);
  // guide lines (e.g. the stone's drop line) while building
  if (build) for (const pl of G.pl) { const pd = Registry.parts[pl.t]; if (pd && pd.guide) pd.guide(ctx, pl); }
  const gd = S.goal && Registry.goals[S.goal.type];
  if (gd) gd.draw(ctx, S.goal!, S.inGoal, G.t, GFX);
  drawCustom('back');
  // fixed parts
  for (const pl of L.fixed || []) { const pd = Registry.parts[pl.t]; if (pd && showPlacement(pd, build)) drawPart(pl, false); }
  drawArt('under', ctx, S, build);
  // the player's parts
  G.pl.forEach((pl, i) => {
    const pd = Registry.parts[pl.t];
    if (!pd || !showPlacement(pd, build)) return;
    const dragging = G.drag && G.drag.idx === i && G.drag.moved;
    let show = pl;
    if (dragging) show = Physics.snap(G.level, G.pl, i);
    ctx.save();
    if (dragging) {
      const ok = Physics.valid(G.level, show) && !G.drag!.overBox;
      ctx.globalAlpha = ok ? 0.9 : 0.45;
      if (show.snapped) { ctx.save(); ctx.beginPath(); ctx.arc(show.x, show.y, 8, 0, 7); ctx.fillStyle = '#fff'; ctx.fill(); ctx.restore(); }
    }
    drawPart(show, G.sel === i && build);
    ctx.restore();
  });
  drawArt('over', ctx, S, build);
  drawCustom('mid');
  const fx = { burst };
  for (const b of S.dyn) if (b.kind === 'hero' || b.kind === 'helper') {
    if (b.dizzy! > 0) b.dizzy!--;
    if (b._px != null) b.roll = (b.roll || 0) + (b.position.x - b._px) / b.circleRadius!;
    b._px = b.position.x;
    // held by an object (e.g. in the toaster) or hidden by the goal (e.g. down in the bowl)
    if (S.custom.some(st => st.def.holds && st.def.holds(st, b))) continue;
    if (gd && gd.hideBall && gd.hideBall(S.goal!, b, fx)) continue;
    GFX.ball(ctx, b, b.kind, G.t);
  }
  if (gd && gd.drawFront) gd.drawFront(ctx, S.goal!, GFX);
  drawCustom('front');
  drawHint();
  drawFx();
}

// ---------- Main loop ----------
export function frame(ts: number) {
  requestAnimationFrame(frame);
  const dt = G.lastTS ? Math.min(100, ts - G.lastTS) : 16.7; G.lastTS = ts;
  if (!G.freeze) G.t++;   // freeze: test shots (#test=...:freeze) are drawn with a fixed G.t
  if (G.bgv !== GFX.bgVer) { G.bgv = GFX.bgVer; if (!$('#scrMap').hidden) openMap(); }
  if (!$('#game').hidden && G.S) {
    if (G.state === 'play' || G.state === 'won' || G.state === 'failing') {
      G.acc += dt;
      let n = 0;
      while (G.acc >= 1000 / 60 && n < 4) { G.S.step(); G.acc -= 1000 / 60; n++; }
      if (n === 4) G.acc = 0;
      handleEvents();
    }
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.fillStyle = '#FFE7C9'; ctx.fillRect(0, 0, innerWidth, innerHeight);
    const sh = G.shake > 0.3 && !G.freeze ? (Math.random() - .5) * G.shake : 0; G.shake *= 0.85;
    ctx.setTransform(DPR * V.s, 0, 0, DPR * V.s, DPR * (V.ox + sh), DPR * V.oy);
    ctx.save();
    GFX.rr(ctx, 0, 0, W, H, 26); ctx.clip();
    drawWorld();
    ctx.restore();
    GFX.rr(ctx, 0, 0, W, H, 26); ctx.lineWidth = 6; ctx.strokeStyle = 'rgba(255,255,255,.8)'; ctx.stroke();
    animHand();
  } else if (!$('#scrTitle').hidden) drawTitle();
  if (!$('#ovIntro').hidden) drawIntro();
}

// Small picture of a level (for the level overview)
// The kit's bodies and objects with a thumb number are drawn in that order; fixed parts with thumb: false are left out.
function thumbPass(x: Ctx2D, S: SimState) {
  const items: { n: number; draw: () => void }[] = [];
  for (const st of S.custom) { const n = st.def.thumb; if (typeof n === 'number' && st.def.draw) items.push({ n, draw: () => st.def.draw!(x, st, 0, GFX, S) }); }
  for (const layer of ['under', 'over'] as const) for (const { kind, art } of arts(layer)) if (art.thumb != null) {
    const n = art.thumb;
    for (const b of S.bodies) if (b.kind === kind) items.push({ n, draw: () => art.draw(x, b, GFX) });
  }
  items.sort((a, b) => a.n - b.n);
  for (const it of items) it.draw();
}
const thumbCache = new Map<string, string>();
export function levelThumb(i: number) {
  const key = i + ':' + GFX.bgVer;
  let src = thumbCache.get(key);
  if (!src) {
    const L = LEVELS[i]; GFX.setWorld(L.wk); const S = Physics.create(Matter, L, []);
    const c = document.createElement('canvas'); c.width = 360; c.height = 240;
    const x = c.getContext('2d')!; x.scale(0.3, 0.3);
    x.drawImage(GFX.background(L, W, H), 0, 0);
    for (const b of S.bodies) if (b.kind === 'static') GFX.staticBody(x, b);
    drawArt('cache', x, S, false);
    const gd = S.goal && Registry.goals[S.goal.type];
    if (gd) gd.draw(x, S.goal!, 0, 0, GFX);
    thumbPass(x, S);
    for (const pl of L.fixed || []) { const pd = Registry.parts[pl.t]; if (pd && pd.draw && pd.thumb !== false) pd.draw(x, pl, false, GFX); }
    for (const ly of ['back', 'mid'] as const) drawCustom(ly, x, S, 0, true);
    for (const b of S.dyn) if (b.kind === 'hero' || b.kind === 'helper') { const bb = Object.create(b); GFX.ball(x, bb, b.kind, 0); }
    drawCustom('front', x, S, 0, true);
    src = c.toDataURL('image/jpeg', 0.8); thumbCache.set(key, src);
  }
  const img = new Image(); img.src = src; img.alt = ''; img.draggable = false;
  return img;
}
